import React from 'react';
import {
    Box,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Button,
    Card,
    CardContent
} from '@mui/material';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { CalculatedResults, PartnershipScenario } from '../types/types';
import SensitivityAnalysis from './SensitivityAnalysis';
import jsPDF from 'jspdf';


ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface ResultsDisplayProps {
    scenario: PartnershipScenario;
    results: CalculatedResults;
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
};

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ scenario, results }) => {
    const roiChartData = {
        labels: results.monthlyProjections.map(p => `Month ${p.month}`),
        datasets: [
            {
                label: 'Cumulative ROI',
                data: results.monthlyProjections.map(p => p.cumulativeROI),
                borderColor: '#33bbdf',
                backgroundColor: 'rgba(51, 187, 223, 0.1)',
                tension: 0.4,
                fill: true
            }
        ]
    };

    const cashFlowChartData = {
        labels: results.quarterlyProjections.map(p => `Q${p.quarter}`),
        datasets: [
            {
                label: 'Investment',
                data: results.quarterlyProjections.map(p => p.investment),
                backgroundColor: 'rgba(51, 187, 223, 0.5)'
            },
            {
                label: 'Returns',
                data: results.quarterlyProjections.map(p => p.returns),
                backgroundColor: 'rgba(78, 205, 196, 0.5)'
            }
        ]
    };

    const exportExecutiveSummary = () => {
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 50;
        const leftMargin = margin;
        const rightMargin = pageWidth - margin;
        let y = margin;

        // Header with professional styling
        doc.setFillColor(51, 187, 223); // Brand blue
        doc.rect(0, 0, pageWidth, 80, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20); // Reduced from 24
        doc.setFont('helvetica', 'bold');
        doc.text('Executive Summary: Partnership ROI', leftMargin, 50);
        
        // Reset text color for body
        doc.setTextColor(0, 0, 0);
        y = 120;

        // Partnership Details Section
        doc.setFontSize(12); // Reduced from 14
        doc.setFont('helvetica', 'bold');
        doc.text('Partnership Details', leftMargin, y);
        y += 25;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        doc.text(`Partnership: ${scenario.name} (${scenario.partnerType})`, leftMargin, y);
        y += 20;
        
        if (scenario.industry) {
            doc.text(`Industry: ${scenario.industry}`, leftMargin, y);
            y += 20;
        }
        
        doc.text(`Timeframe: ${scenario.timeframe} years`, leftMargin, y);
        y += 35;

        // Headline Metrics Section
        doc.setFontSize(12); // Reduced from 14
        doc.setFont('helvetica', 'bold');
        doc.text('Headline Metrics', leftMargin, y);
        y += 25;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        // Create a metrics box
        const metricsBoxWidth = 280; // Increased width to accommodate longer labels
        const metricsBoxHeight = 60;
        const metricsBoxX = leftMargin;
        const metricsBoxY = y;
        
        doc.setDrawColor(51, 187, 223);
        doc.setLineWidth(2);
        doc.rect(metricsBoxX, metricsBoxY, metricsBoxWidth, metricsBoxHeight);
        
        // Base ROI
        doc.setFontSize(14); // Reduced from 16
        doc.setFont('helvetica', 'bold');
        doc.text('Base ROI:', metricsBoxX + 15, metricsBoxY + 25);
        doc.setTextColor(51, 187, 223); // Blue
        doc.text(`${formatPercentage(results.baseROIPercentage)}`, metricsBoxX + 120, metricsBoxY + 25);
        
        // Risk-Adjusted ROI
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(14); // Reduced from 16
        doc.setFont('helvetica', 'bold');
        doc.text('Risk-Adjusted ROI:', metricsBoxX + 15, metricsBoxY + 45);
        doc.setTextColor(78, 205, 196); // Teal
        doc.text(`${formatPercentage(results.riskAdjustedROIPercentage)}`, metricsBoxX + 160, metricsBoxY + 45);
        
        // Reset colors
        doc.setTextColor(0, 0, 0);
        y += metricsBoxHeight + 25;
        
        // Payback Period
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        doc.text(`Payback Period: ${results.breakEvenMonths} months`, leftMargin, y);
        y += 35;

        // Executive Summary Section
        doc.setFontSize(12); // Reduced from 14
        doc.setFont('helvetica', 'bold');
        doc.text('Executive Summary', leftMargin, y);
        y += 25;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        // Summary text - ensure clean text without special characters
        const summaryText = `This partnership demonstrates strong financial viability with a ${formatPercentage(results.baseROIPercentage)} return on investment. The ${results.breakEvenMonths}-month payback period indicates rapid value realization, while the risk-adjusted ROI of ${formatPercentage(results.riskAdjustedROIPercentage)} accounts for market uncertainties. Total returns of ${formatCurrency(results.totalReturns)} significantly outweigh the ${formatCurrency(results.totalInvestment)} investment, making this an attractive opportunity for stakeholders.`;
        
        // Clean the text to ensure no special characters cause issues
        const cleanSummaryText = summaryText.replace(/[^\x20-\x7E]/g, '').trim();
        
        // Use full page width with proper text wrapping to prevent cutoff
        const summaryBoxWidth = pageWidth - (leftMargin + 50); // Full width minus small margins
        const summaryBoxX = leftMargin;
        const summaryBoxY = y;
        
        // Set a fixed height for the summary box without borders
        const summaryBoxHeight = 100; // Reduced height for tighter layout
        
        // Only background fill, no border (removes the blue vertical line)
        doc.setFillColor(248, 249, 250); // Light gray background
        doc.rect(summaryBoxX, summaryBoxY, summaryBoxWidth, summaryBoxHeight, 'F');
        
        // Use jsPDF's built-in text wrapping for better text flow
        const textOptions = {
            maxWidth: summaryBoxWidth - 30, // Leave 15px margin on each side
            align: 'left'
        };
        
        // Render text with proper wrapping and minimal spacing
        doc.text(cleanSummaryText, summaryBoxX + 15, summaryBoxY + 10, textOptions);
        
        // Update y position with very minimal spacing to Strategic Recommendations
        y = summaryBoxY + summaryBoxHeight + 2; // Minimal gap between sections

        // Recommendations Section
        doc.setFontSize(12); // Reduced from 14
        doc.setFont('helvetica', 'bold');
        doc.text('Strategic Recommendations', leftMargin, y);
        y += 25;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        const recommendations = [
            'PROCEED with partnership implementation given the strong ROI and quick payback period',
            'MONITOR quarterly performance against projections, especially in Q1-Q2 ramp-up phase',
            'ALLOCATE resources according to the phased investment approach outlined in the plan',
            'ESTABLISH regular partner performance reviews to maintain momentum and address risks early'
        ];
        
        recommendations.forEach((rec, index) => {
            // Simple text rendering without any text processing to avoid vertical stacking
            doc.setFontSize(10);
            doc.text(rec, leftMargin + 35, y);
            y += 25; // Fixed spacing between recommendations
        });
        
        y += 25; // Add extra spacing before next section

        // Financial Benefits Section
        doc.setFontSize(12); // Reduced from 14
        doc.setFont('helvetica', 'bold');
        doc.text('Top Financial Benefits', leftMargin, y);
        y += 25;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        const benefits = [
            'Revenue growth from partner-sourced pipeline',
            'Cost savings from shared marketing and enablement',
            'Efficiency gains via shorter sales cycles'
        ];
        
        benefits.forEach((benefit, index) => {
            doc.text(`• ${benefit}`, leftMargin + 35, y); // Increased left margin significantly
            y += 20;
        });
        y += 15;

        // Risks & Mitigation Section
        doc.setFontSize(12); // Reduced from 14
        doc.setFont('helvetica', 'bold');
        doc.text('Key Risks & Mitigation', leftMargin, y);
        y += 25;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        const risks = [
            'Partner churn: mitigate with tiered incentives and QBRs',
            'Resource allocation: mitigate with phased investments'
        ];
        
        risks.forEach((risk, index) => {
            doc.text(`• ${risk}`, leftMargin + 35, y); // Increased left margin significantly
            y += 20;
        });
        y += 35;



        // Methodology Section
        doc.setFontSize(12); // Restored to readable size
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100); // Gray text
        const methodologyText = 'Methodology: ROI computed from monthly cash flows with 10% annual discounting; market expansion, retention, and cycle factors applied; risk adjustments per user selection.';
        
        // Render the text and get the actual height used
        const methodologyLines = doc.splitTextToSize(methodologyText, pageWidth - leftMargin - rightMargin - 40);
        const methodologyHeight = methodologyLines.length * 14; // Approximate line height
        
        methodologyLines.forEach((line: string, index: number) => {
            if (line && line.trim()) { // Only render non-empty lines
                doc.text(line.trim(), leftMargin + 20, y + (index * 16));
            }
        });
        
        // Update y position based on actual text height used
        y += methodologyHeight + 40; // Add extra spacing before footer

        // Footer
        doc.setTextColor(150, 150, 150);
        doc.setFontSize(10); // Restored to readable size
        doc.text(`Generated on ${new Date().toLocaleDateString()}`, leftMargin, y);
        
        // Copyright notice
        doc.text('©Clever Data Consulting', leftMargin, y + 20);

        doc.save(`${scenario.name}-executive-summary.pdf`);
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 50;
        let y = margin;

        // Header with professional styling
        doc.setFillColor(51, 187, 223); // Brand blue
        doc.rect(0, 0, pageWidth, 80, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20); // Reduced from 24
        doc.setFont('helvetica', 'bold');
        doc.text('Partnership ROI Analysis', margin, 50);
        
        // Reset text color for body
        doc.setTextColor(0, 0, 0);
        y = 120;

        // Scenario Information Section
        doc.setFontSize(14); // Reduced from 16
        doc.setFont('helvetica', 'bold');
        doc.text('Scenario Information', margin, y);
        y += 30;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        doc.text(`Scenario: ${scenario.name}`, margin, y);
        y += 20;
        doc.text(`Partner Type: ${scenario.partnerType}`, margin, y);
        y += 20;
        doc.text(`Timeframe: ${scenario.timeframe} years`, margin, y);
        y += 20;
        if (scenario.industry) {
            doc.text(`Industry: ${scenario.industry}`, margin, y);
            y += 20;
        }
        y += 20;

        // Key Metrics Section
        doc.setFontSize(14); // Reduced from 16
        doc.setFont('helvetica', 'bold');
        doc.text('Key Metrics', margin, y);
        y += 30;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        // Create a metrics table with better formatting
        const tableX = margin;
        const tableY = y;
        const colWidth = 200;
        const rowHeight = 25;
        
        // Table headers
        doc.setFont('helvetica', 'bold');
        doc.text('Metric', tableX, tableY);
        doc.text('Value', tableX + colWidth, tableY);
        y += rowHeight;
        
        // Table rows
        doc.setFont('helvetica', 'normal');
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        
        const metrics = [
            ['Total Investment', formatCurrency(results.totalInvestment)],
            ['Total Returns', formatCurrency(results.totalReturns)],
            ['Base ROI', formatPercentage(results.baseROIPercentage)],
            ['Break-even Period', `${results.breakEvenMonths} months`],
            ['Net Present Value', formatCurrency(results.netPresentValue)],
            ['Risk-adjusted ROI', formatPercentage(results.riskAdjustedROIPercentage)]
        ];
        
        metrics.forEach(([label, value], index) => {
            // Draw row separator
            doc.line(tableX, tableY + (index + 1) * rowHeight, tableX + colWidth * 2, tableY + (index + 1) * rowHeight);
            
            doc.text(label, tableX, tableY + (index + 1) * rowHeight - 8);
            doc.text(value, tableX + colWidth, tableY + (index + 1) * rowHeight - 8);
        });
        
        y = tableY + (metrics.length + 1) * rowHeight + 30;

        // Executive Summary Section
        doc.setFontSize(14); // Reduced from 16
        doc.setFont('helvetica', 'bold');
        doc.text('Executive Summary', margin, y);
        y += 30;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        // Summary text - ensure clean text without special characters
        const summaryText2 = `This partnership scenario shows a ${formatPercentage(results.baseROIPercentage)} return on investment with a ${results.breakEvenMonths}-month payback period. The risk-adjusted ROI of ${formatPercentage(results.riskAdjustedROIPercentage)} reflects market uncertainties, while the positive NPV of ${formatCurrency(results.netPresentValue)} confirms long-term value.`;
        
        // Clean the text to ensure no special characters cause issues
        const cleanSummaryText2 = summaryText2.replace(/[^\x20-\x7E]/g, '').trim();
        
        // Create a summary box with dynamic height
        const summaryBoxWidth2 = pageWidth - (margin * 2);
        const summaryBoxX2 = margin;
        const summaryBoxY2 = y;
        
        // Use jsPDF's built-in text wrapping to avoid vertical character stacking
        // Set a fixed height for the summary box and let jsPDF handle text flow
        const summaryBoxHeight2 = 80; // Fixed height to ensure consistent layout
        
        doc.setFillColor(248, 249, 250); // Light gray background
        doc.rect(summaryBoxX2, summaryBoxY2, summaryBoxWidth2, summaryBoxHeight2, 'F');
        doc.setDrawColor(51, 187, 223);
        doc.setLineWidth(1);
        doc.rect(summaryBoxX2, summaryBoxY2, summaryBoxWidth2, summaryBoxHeight2);
        
        // Render the entire text as one block with automatic wrapping
        // This should eliminate vertical character stacking completely
        doc.text(cleanSummaryText2, summaryBoxX2 + 30, summaryBoxY2 + 20, {
            maxWidth: summaryBoxWidth2 - 60,
            align: 'left'
        });
        
        // Update y position based on the full box height to ensure proper spacing
        y = summaryBoxY2 + summaryBoxHeight2 + 20;

        // Key Insights Section
        doc.setFontSize(14); // Reduced from 16
        doc.setFont('helvetica', 'bold');
        doc.text('Key Insights', margin, y);
        y += 30;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        const insights = [
            `• Investment of ${formatCurrency(results.totalInvestment)} generates ${formatCurrency(results.totalReturns)} in returns`,
            `• Break-even achieved in ${results.breakEvenMonths} months, indicating rapid value realization`,
            `• Risk-adjusted approach reduces ROI by ${(results.baseROIPercentage - results.riskAdjustedROIPercentage).toFixed(1)} percentage points`,
            `• Positive NPV of ${formatCurrency(results.netPresentValue)} confirms long-term financial viability`
        ];
        
        insights.forEach((insight, index) => {
            doc.text(insight, margin + 35, y); // Increased left margin significantly
            y += 20;
        });
        
        y += 20;

        // Recommendations Section
        doc.setFontSize(14); // Reduced from 16
        doc.setFont('helvetica', 'bold');
        doc.text('Recommendations', margin, y);
        y += 30;
        
        doc.setFontSize(10); // Reduced from 12
        doc.setFont('helvetica', 'normal');
        
        const recommendations = [
            'PROCEED with implementation given strong financial metrics',
            'MONITOR performance against quarterly projections',
            'MAINTAIN risk mitigation strategies throughout execution',
            'REVIEW and adjust assumptions based on actual performance data'
        ];
        
        recommendations.forEach((rec, index) => {
            // Simple text rendering without any text processing to avoid vertical stacking
            doc.setFontSize(10);
            doc.text(rec, margin + 35, y);
            y += 25; // Fixed spacing between recommendations
        });
        
        y += 25; // Add extra spacing after recommendations

        // Footer
        doc.setTextColor(150, 150, 150);
        doc.setFontSize(10);
        doc.text(`Generated on ${new Date().toLocaleDateString()}`, margin, y);
        doc.text('©Clever Data Consulting', margin, y + 20);
        
        // Save the PDF
        console.log('Attempting to save PDF...');
        doc.save(`${scenario.name}-roi-analysis.pdf`);
        console.log('PDF save command executed');
        
        } catch (error) {
            console.error('Error in PDF generation:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            alert(`PDF generation failed: ${errorMessage}`);
        }
    };




    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom sx={{ 
                        color: '#33bbdf',
                        fontWeight: 700,
                        textAlign: 'center',
                        mb: 4
                    }}>
                        ROI Analysis Results
                    </Typography>
                </Grid>

                {/* Key Metrics */}
                <Grid item xs={12} md={6}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 2,
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                        }
                    }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: '#33bbdf', fontWeight: 600 }}>
                                Key Metrics
                            </Typography>
                            <TableContainer>
                                <Table>
                                <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Total Investment</TableCell>
                                            <TableCell align="right">{formatCurrency(results.totalInvestment)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Total Returns</TableCell>
                                            <TableCell align="right">{formatCurrency(results.totalReturns)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Base ROI</TableCell>
                                            <TableCell align="right">{formatPercentage(results.baseROIPercentage)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Break-even Period</TableCell>
                                            <TableCell align="right">{results.breakEvenMonths} months</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Net Present Value</TableCell>
                                            <TableCell align="right">{formatCurrency(results.netPresentValue)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Risk-adjusted ROI</TableCell>
                                            <TableCell align="right">{formatPercentage(results.riskAdjustedROIPercentage)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Charts */}
                <Grid item xs={12} md={6}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 2,
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                        }
                    }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: '#33bbdf', fontWeight: 600 }}>
                                ROI Over Time
                            </Typography>
                            <Box sx={{ height: 300 }}>
                                <Line
                                    data={roiChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            title: {
                                                display: true,
                                                text: 'Cumulative ROI Progression',
                                                font: {
                                                    size: 16,
                                                    weight: 'bold'
                                                }
                                            },
                                            legend: {
                                                position: 'top',
                                            }
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                grid: {
                                                    color: 'rgba(0, 0, 0, 0.1)'
                                                }
                                            },
                                            x: {
                                                grid: {
                                                    color: 'rgba(0, 0, 0, 0.1)'
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card elevation={3} sx={{ 
                        borderRadius: 2,
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                        }
                    }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: '#33bbdf', fontWeight: 600 }}>
                                Quarterly Investment vs Returns
                            </Typography>
                            <Box sx={{ height: 300 }}>
                                <Bar
                                    data={cashFlowChartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            title: {
                                                display: true,
                                                text: 'Quarterly Cash Flow Analysis',
                                                font: {
                                                    size: 16,
                                                    weight: 'bold'
                                                }
                                            },
                                            legend: {
                                                position: 'top',
                                            }
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                grid: {
                                                    color: 'rgba(0, 0, 0, 0.1)'
                                                }
                                            },
                                            x: {
                                                grid: {
                                                    color: 'rgba(0, 0, 0, 0.1)'
                                                }
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Sensitivity Analysis */}
                <Grid item xs={12}>
                    <Card elevation={3} sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <SensitivityAnalysis scenario={scenario} baselineResults={results} />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Export Buttons */}
                <Grid item xs={12}>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        justifyContent: 'center',
                        mt: 3
                    }}>
                        <Button
                            variant="contained"
                            onClick={exportToPDF}
                            sx={{
                                bgcolor: '#33bbdf',
                                '&:hover': {
                                    bgcolor: '#2a9bb8',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            Export to PDF
                        </Button>
                        


                        <Button
                            variant="outlined"
                            onClick={exportExecutiveSummary}
                        >
                            Executive Summary (PDF)
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ResultsDisplay; 